import { useState, useEffect, useRef, useMemo } from 'react';
import { FiEyeOff } from 'react-icons/fi';
import { FaUsers } from 'react-icons/fa';
import { IoCloseCircleSharp } from 'react-icons/io5';
import {
  MdOutlineArrowDropDown,
  MdSubject,
  MdOutlineContentCopy,
} from 'react-icons/md';
import { BsPaperclip } from 'react-icons/bs';
import { LiaGoogleDrive } from 'react-icons/lia';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaRegTrashAlt } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAudio,
  FaFileVideo,
  FaFileArchive,
  FaFile,
} from 'react-icons/fa';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import useDrivePicker from 'react-google-drive-picker';

import { draftEmail, sendEmail } from '../../services/email_api';
import { DataState } from '../../context/DataProvider';
import { google_client_id, google_api_key } from '../../utils/keys';

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf'))
    return <FaFilePdf className="text-red-500" size={20} />;
  if (mimeType?.includes('word'))
    return <FaFileWord className="text-[#5652CF]" size={20} />;
  if (mimeType?.includes('excel'))
    return <FaFileExcel className="text-green-500" size={20} />;
  if (mimeType?.includes('image'))
    return <FaFileImage className="text-yellow-500" size={20} />;
  if (mimeType?.includes('zip') || mimeType?.includes('rar'))
    return <FaFileArchive className="text-gray-500" size={20} />;
  if (mimeType?.includes('audio'))
    return <FaFileAudio className="text-purple-500" size={20} />;
  if (mimeType?.includes('video'))
    return <FaFileVideo className="text-indigo-500" size={20} />;
  return <FaFile className="text-gray-500" size={20} />;
};

const AttachmentItem = ({ file, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const url = URL.createObjectURL(file?.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file?.file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="inline-flex h-10 w-40 cursor-pointer items-center justify-center rounded-lg border p-2 py-3 text-gray-500 shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered ? (
        <div className="flex w-full items-center justify-center gap-4">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-gray-600"
              size={16}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-gray-600 hover:text-black"
              size={16}
            />
          )}

          <button
            onClick={() => onRemove(file)}
            className="ml-2 text-gray-600 hover:text-red-400"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="flex w-full items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center">
            {getFileIcon(file?.file.mimeType || file?.file.type)}{' '}
            {/* Handle both cases */}
          </div>
          <span className="truncate text-sm font-medium">
            {file?.file.filename || file?.file.name} {/* Handle both cases */}
          </span>
        </div>
      )}
    </div>
  );
};

const ComposeEmail = ({
  setIsComposeEmailModalOpen,
  message_id,
  isOutlook,
}) => {
  const [messageBody, setMessageBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const { userGoogleId } = DataState();
  const [loading, setLoading] = useState(false);
  const [recepientsEmails, setRecepientsEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [inputCcValue, setInputCcValue] = useState('');
  const [inputBccValue, setInputBccValue] = useState('');
  const [customPromptLoading, setCustomPromptLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const fileInputRef = useRef(null);
  const [openPicker] = useDrivePicker();

  const [subject, setSubject] = useState('');
  const [ccs, setCcs] = useState([]);
  const [bccs, setBccs] = useState([]);

  const [driveAttachments, setDriveAttachments] = useState([]);
  const [localAttachments, setLocalAttachments] = useState([]);

  const attachments = useMemo(() => {
    return [
      ...localAttachments.map((file) => ({
        name: file.name,
        from: 'local',
        file,
      })),
      ...driveAttachments.map((file) => ({
        name: file.name,
        from: 'drive',
        id: file.id,
        file,
      })),
    ];
  }, [localAttachments, driveAttachments]);

  const handleTrashClick = () => {
    if (
      !recepientsEmails.length &&
      !ccs.length &&
      !bccs.length &&
      !subject &&
      !messageBody &&
      !customPrompt &&
      !localAttachments.length &&
      !driveAttachments.length
    ) {
      setIsComposeEmailModalOpen(false); // Close the modal if everything is already empty
      return;
    }

    // Reset all fields
    setRecepientsEmails([]);
    setCcs([]);
    setBccs([]);
    setSubject('');
    setMessageBody('');
    setCustomPrompt('');
    setLocalAttachments([]);
    setDriveAttachments([]);
    setInputValue('');
    setInputCcValue('');
    setInputBccValue('');
  };

  const validEmailProviders = [
    'gmail.com',
    'outlook.com',
    'yahoo.com',
    'hotmail.com',
    'icloud.com',
    'protonmail.com',
    'zoho.com',
    'aol.com',
    'gmx.com',
  ];

  const modules = {
    toolbar: [
      [{ font: [] }, { size: [] }], // Font and Size
      ['bold', 'italic', 'underline', 'strike'], // Formatting
      [{ color: [] }, { background: [] }], // Text color & Background color
      [{ list: 'ordered' }, { list: 'bullet' }], // Ordered & Unordered lists
      [{ align: [] }], // Alignment options
      ['blockquote'], // Blockquote & Code block
      ['link', 'image'], // Links & Images
      ['clean'], // Remove formatting
    ],
  };

  const formats = [
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'blockquote',
    'link',
    'image',
    'clean',
  ];

  // Add dark mode styles for ReactQuill
  useEffect(() => {
    // Add custom CSS for dark mode Quill editor
    const style = document.createElement('style');
    style.innerHTML = `
      .ql-snow .ql-stroke {
        stroke: #a1a1aa;
      }
      .ql-snow .ql-fill {
        fill: #a1a1aa;
      }
      .ql-snow .ql-picker {
        color: #a1a1aa;
      }
      .ql-snow .ql-picker-options {
        background-color: #27272a;
        border-color: #3f3f46;
      }
      .ql-snow.ql-toolbar button:hover, 
      .ql-snow .ql-toolbar button:hover, 
      .ql-snow.ql-toolbar button.ql-active, 
      .ql-snow .ql-toolbar button.ql-active, 
      .ql-snow.ql-toolbar .ql-picker-label:hover, 
      .ql-snow .ql-toolbar .ql-picker-label:hover, 
      .ql-snow.ql-toolbar .ql-picker-label.ql-active, 
      .ql-snow .ql-toolbar .ql-picker-label.ql-active, 
      .ql-snow.ql-toolbar .ql-picker-item:hover, 
      .ql-snow .ql-toolbar .ql-picker-item:hover, 
      .ql-snow.ql-toolbar .ql-picker-item.ql-selected, 
      .ql-snow .ql-toolbar .ql-picker-item.ql-selected {
        color: #d4d4d8;
      }
      .ql-snow.ql-toolbar button:hover .ql-stroke, 
      .ql-snow .ql-toolbar button:hover .ql-stroke, 
      .ql-snow.ql-toolbar button.ql-active .ql-stroke, 
      .ql-snow .ql-toolbar button.ql-active .ql-stroke, 
      .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke, 
      .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke, 
      .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke, 
      .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke, 
      .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke, 
      .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke, 
      .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke, 
      .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke {
        stroke: #d4d4d8;
      }
      .ql-snow.ql-toolbar, .ql-snow .ql-toolbar {
        border-color: #3f3f46;
        background-color: #27272a;
      }
      .ql-container.ql-snow {
        border-color: #3f3f46;
      }
      .ql-editor {
        color: #d4d4d8;
        background-color: #18181b;
      }
      .ql-editor.ql-blank::before {
        color: #71717a;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const removeEmail = (index) => {
    setRecepientsEmails(recepientsEmails.filter((_, i) => i !== index));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setLocalAttachments((prevAttachments) => [...prevAttachments, ...files]);
  };

  const handleRemove = (fileToRemove) => {
    if (fileToRemove.from === 'local') {
      setLocalAttachments((prev) =>
        prev.filter((file) => file.name !== fileToRemove.name),
      );
    } else if (fileToRemove.from === 'drive') {
      setDriveAttachments((prev) =>
        prev.filter((file) => file.id !== fileToRemove.id),
      );
    }
  };

  const handlePaperClipClick = () => {
    fileInputRef.current.click();
  };

  const handleKeyDown = (event) => {
    if (event.key === ',' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const email = inputValue.trim().replace(/,$/, '');

      // Email pattern: username@provider.domain
      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      const match = email.match(emailPattern);

      if (email && !recepientsEmails.includes(email)) {
        if (match) {
          const domain = match[1];

          if (validEmailProviders.includes(domain)) {
            setRecepientsEmails([...recepientsEmails, email]);
            setInputValue('');
          } else {
            toast.error('Invalid email provider!');
          }
        } else {
          toast.error('Invalid email format!');
        }
      }
    }
  };
  const handleCcKeyDown = (event) => {
    if (event.key === ',' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const email = inputCcValue.trim().replace(/,$/, '');

      // Email pattern: username@provider.domain
      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      const match = email.match(emailPattern);

      if (email && !ccs.includes(email)) {
        if (match) {
          const domain = match[1];

          if (validEmailProviders.includes(domain)) {
            setCcs([...ccs, email]);
            setInputCcValue('');
          } else {
            toast.error('Invalid email provider!');
          }
        } else {
          toast.error('Invalid email format!');
        }
      }
    }
  };
  const handleBccKeyDown = (event) => {
    if (event.key === ',' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const email = inputBccValue.trim().replace(/,$/, '');

      // Email pattern: username@provider.domain
      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      const match = email.match(emailPattern);

      if (email && !bccs.includes(email)) {
        if (match) {
          const domain = match[1];

          if (validEmailProviders.includes(domain)) {
            setBccs([...bccs, email]);
            setInputBccValue('');
          } else {
            toast.error('Invalid email provider!');
          }
        } else {
          toast.error('Invalid email format!');
        }
      }
    }
  };
  const handleDraftEmail = async () => {
    setCustomPromptLoading(true);

    const requestBody = {
      custom_prompt: customPrompt || undefined,
      previous_email_body: messageBody || undefined,
      previous_subject: subject || undefined,
      recipients: recepientsEmails.length > 0 ? recepientsEmails : undefined,
    };
    try {
      const response = await draftEmail(userGoogleId, requestBody, isOutlook);

      setMessageBody(response?.html_body || '');
      setSubject(response?.subject || '');

      toast.success('Email Draft successfully!');
    } catch (error) {
      toast.error('Failed to draft Email . Try again.');
    } finally {
      setCustomPromptLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setLoading(true);

    if (!recepientsEmails.length || !subject || !messageBody) {
      toast.error('Please fill all required fields.');
      setLoading(false);
      return;
    }

    const formData = new FormData();

    // Append each recipient separately
    recepientsEmails.forEach((email) => formData.append('recipients', email));

    formData.append('subject', subject);
    formData.append('message_body', messageBody);
    formData.append('user_google_id', userGoogleId);

    // Conditionally append CCs if they exist and are non-empty
    if (ccs && ccs.length > 0) {
      ccs.forEach((email) => formData.append('ccs', email));
    }

    // Conditionally append BCCs if they exist and are non-empty
    if (bccs && bccs.length > 0) {
      bccs.forEach((email) => formData.append('bccs', email));
    }

    // Append attachments if present
    //local attachments
    if (localAttachments.length > 0) {
      localAttachments.forEach((file) => {
        formData.append('files', file);
      });
    }
    // drive attachments
    const driveFileIds = driveAttachments.map((file) => file.id);
    driveFileIds.forEach((id) => formData.append('google_drive_file_ids', id));

    try {
      await sendEmail(userGoogleId, formData, isOutlook);
      toast.success('Email sent successfully!');
      setMessageBody('');
      setLocalAttachments([]);
      setDriveAttachments([]);
      setRecepientsEmails([]);
      setCcs([]);
      setBccs([]);
      setCustomPrompt('');
      setSubject('');
      setInputValue('');
      setInputCcValue('');
      setInputBccValue('');
    } catch (error) {
      toast.error('Failed to send email.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPicker = () => {
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: 'DOCS',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: (data) => {
        if (data.action === 'picked') {
          const files = data.docs.map((file) => ({
            id: file.id,
            name: file.name,
            mimeType: file?.mimeType || file?.type,
            url: file.url,
          }));
          setDriveAttachments((prev) => [...prev, ...files]);
        } else {
        }
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex w-full items-center justify-center">
      {/* Blurry Background Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"></div>
      <div className="scrollbar-none absolute top-20 flex max-h-[80vh] w-5/6 flex-row overflow-y-auto sm:w-2/3">
        <div className="flex w-full flex-col">
          <div className="w-full rounded-xl border border-zinc-700 bg-zinc-800 p-4 shadow-md">
            <div className="mb-6 flex justify-between gap-1 text-zinc-300 sm:items-center">
              <div className="flex gap-2 text-lg font-semibold text-zinc-300 sm:items-center">
                {isOutlook ? (
                  <img
                    src="/assets/icons8-microsoft-outlook.svg"
                    alt="Outlook"
                    className="h-7 w-7"
                  />
                ) : (
                  <img
                    src="/assets/icons8-gmail.svg"
                    alt="Gmail"
                    className="h-7 w-7"
                  />
                )}
                Compose an Email
              </div>
              {/* eslint-disable-next-line */}
              <div
                onClick={() => {
                  setIsComposeEmailModalOpen(false);
                }}
                className="flex rounded-xl p-1 transition-colors duration-200 hover:bg-zinc-700 sm:items-center"
              >
                <IoCloseCircleSharp size={20} />
              </div>
            </div>
            <div className="mb-3  flex items-center justify-start gap-1 text-zinc-300">
              <div className="flex w-full flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <FaUsers className="hidden h-5 w-5 self-start text-zinc-400 sm:block sm:self-center" />

                <div className="flex min-h-[40px] w-full flex-grow flex-wrap items-center gap-1 rounded-lg border border-zinc-700 p-2 text-sm">
                  {recepientsEmails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-lg bg-zinc-300 px-2 py-1 text-gray-600"
                    >
                      {email}
                      <button
                        onClick={() => removeEmail(index)}
                        className="ml-2 text-gray-600 hover:text-red-400"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="min-w-[120px] flex-grow bg-transparent text-zinc-300 outline-none"
                    placeholder="Enter recipients..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                <div className="flex gap-2">
                  {/* eslint-disable-next-line */}
                  <div
                    onClick={() => setShowCc(!showCc)}
                    className={`flex min-h-[40px] items-center justify-center rounded-lg border border-zinc-700 px-2 py-1 text-sm transition-colors duration-200 hover:bg-zinc-700
      ${showCc ? 'bg-zinc-700' : 'bg-zinc-800'}
      w-full text-center sm:w-auto
    `}
                  >
                    Cc
                  </div>

                  {/* eslint-disable-next-line */}
                  <div
                    onClick={() => setShowBcc(!showBcc)}
                    className={`flex min-h-[40px] items-center justify-center rounded-lg border border-zinc-700 px-2 py-1 text-sm transition-colors duration-200 hover:bg-zinc-700
      ${showBcc ? 'bg-zinc-700' : 'bg-zinc-800'}
      w-full text-center sm:w-auto
    `}
                  >
                    Bcc
                  </div>
                </div>
              </div>
            </div>

            {showCc && (
              <div className="mb-3  flex items-center justify-start gap-1 text-zinc-300">
                <div className="flex w-full items-center justify-between sm:space-x-2">
                  <MdOutlineContentCopy className="hidden h-5 w-5 text-zinc-400 sm:block" />
                  <div className="flex min-h-[40px] flex-grow flex-wrap items-center gap-1  rounded-lg border border-zinc-700 p-1 text-sm">
                    {ccs.map((email, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-lg bg-zinc-300 px-2 py-1 text-gray-600"
                      >
                        {email}
                        <button
                          onClick={() => {
                            setCcs(ccs.filter((_, i) => i !== index));
                          }}
                          className="ml-2 text-gray-600 hover:text-red-400"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      className="ml-1 min-w-[100px] flex-grow bg-transparent  text-zinc-300 outline-none"
                      placeholder="Cc..."
                      value={inputCcValue}
                      onChange={(e) => setInputCcValue(e.target.value)}
                      onKeyDown={handleCcKeyDown}
                    />
                  </div>
                </div>
              </div>
            )}
            {showBcc && (
              <div className="mb-3  flex items-center justify-start gap-1 text-zinc-300">
                <div className="flex w-full items-center justify-between sm:space-x-2">
                  <FiEyeOff className="hidden h-5 w-5 text-zinc-400 sm:block" />
                  <div className="flex min-h-[40px] flex-grow flex-wrap items-center gap-1  rounded-lg border border-zinc-700 p-1 text-sm">
                    {bccs.map((email, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-lg bg-zinc-300 px-2 py-1 text-gray-600"
                      >
                        {email}
                        <button
                          onClick={() => {
                            setBccs(bccs.filter((_, i) => i !== index));
                          }}
                          className="ml-2 text-gray-600 hover:text-red-400"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      className="ml-1 min-w-[100px] flex-grow bg-transparent text-zinc-300 outline-none"
                      placeholder="bcc..."
                      value={inputBccValue}
                      onChange={(e) => setInputBccValue(e.target.value)}
                      onKeyDown={handleBccKeyDown}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mb-3  flex items-center justify-start gap-1 text-zinc-300">
              <div className="flex w-full items-center justify-between sm:space-x-2">
                <MdSubject className="hidden h-5 w-5 text-zinc-400 sm:block" />
                <div className="flex min-h-[40px] flex-grow flex-wrap items-center gap-1  rounded-lg border border-zinc-700 p-1 text-sm">
                  <input
                    type="text"
                    className="ml-1 min-w-[100px] flex-grow bg-transparent text-zinc-300 outline-none"
                    placeholder="Subject..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <ReactQuill
              value={messageBody}
              onChange={setMessageBody}
              modules={modules}
              formats={formats}
              className="text-md  w-full text-zinc-300"
              theme="snow"
            />

            {attachments && attachments.length > 0 && (
              <div className="mt-2 flex flex-grow flex-wrap items-center  gap-2 p-1">
                {attachments.map((file, index) => (
                  <AttachmentItem
                    key={index}
                    file={file}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            )}

            <div className="mt-3 flex flex-col items-center justify-between text-zinc-300">
              <div className="flex w-full flex-col items-stretch justify-between space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
                <input
                  type="text"
                  name="custom prompt"
                  value={customPrompt}
                  placeholder="Add prompt for AI-generated email draft..."
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
                <button
                  onClick={handleDraftEmail}
                  disabled={customPromptLoading}
                  className="flex w-full items-center justify-center space-x-2 rounded-md border border-zinc-600 bg-zinc-700 px-4 py-2 text-sm text-white transition hover:border-zinc-700 hover:bg-zinc-800 sm:w-auto"
                >
                  <img src="/assets/logo.png" alt="Logo" className="h-5 w-5" />
                  {customPromptLoading ? (
                    <span>Generating...</span>
                  ) : (
                    <span>Generate</span>
                  )}
                </button>
              </div>

              <div className="flex w-full items-center justify-between gap-2 space-x-3 p-2 pl-0 pr-0 sm:gap-0">
                <div className="flex items-center space-x-3">
                  {/* Send Button */}
                  <button
                    onClick={handleSendEmail}
                    disabled={loading}
                    className="flex items-center justify-between rounded-full bg-zinc-700 px-2 font-medium text-zinc-100 transition-colors hover:bg-zinc-600"
                  >
                    <div className="border-r-[1px] border-zinc-600 py-1 pl-2 pr-4">
                      {loading ? 'Sending...' : 'Send'}
                    </div>
                    <div className="flex h-full items-center justify-center pl-1">
                      <MdOutlineArrowDropDown size={20} />
                    </div>
                  </button>

                  {/* Toolbar Icons */}
                  <div className="flex">
                    {/* eslint-disable-next-line */}
                    <div
                      onClick={handlePaperClipClick}
                      className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
                    >
                      <BsPaperclip size={18} />

                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileChange}
                        multiple
                      />
                    </div>
                    {!isOutlook && (
                      // eslint-disable-next-line
                      <div
                        onClick={handleOpenPicker}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
                      >
                        <LiaGoogleDrive size={18} />
                      </div>
                    )}

                    <div className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200">
                      <BsThreeDotsVertical size={18} />
                    </div>
                  </div>
                </div>

                {/* Trash Icon on the Right */}
                <div className="ml-auto w-auto">
                  <button
                    onClick={handleTrashClick}
                    className="text-zinc-400 transition-colors hover:text-zinc-200"
                  >
                    <FaRegTrashAlt size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeEmail;
